import {
  ChangeDetectionStrategy,
  Component,
  computed, inject,
  input,
  output,
} from '@angular/core';

import { IconComponent } from '../../design-system/components/icon/icon.component';

import {
  AnimalEventStatus,
  resolveAnimalEventStatus,
} from '../../utils/animal-event-status.util';
import { SharedPetUtilsService } from "../../services/shared-pet-utils.service";
import { DatesHandlerService } from "../../services/dates-handler.service";
import { NewLoadingComponent } from "../../new-loading/new-loading.component";
import { AnimalHistory, AnimalHistoryDisplayItem } from "./survey-history.model";
import { RouterLink } from "@angular/router";
import { SurveySubmission } from "../../shared-models/survey-submission.model";
import { CheckInsSummaryComponent } from "../../check-ins-summary/check-ins-summary.component";
import * as _ from "lodash";
import { generateCheckInStatusIcon } from "../../utils/check-ins-summary.util";
import { CheckInSummaryModel } from "../../check-ins-summary/check-ins-statuses";

@Component({
  selector: 'lib-survey-history',
  standalone: true,
  imports: [
    IconComponent,
    NewLoadingComponent,
    RouterLink,
    CheckInsSummaryComponent
  ],
  templateUrl: './survey-history.component.html',
  styleUrl: './survey-history.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SurveyHistoryComponent {
  private sharedPetUtilsService = inject(SharedPetUtilsService);
  private datesHandlerService = inject(DatesHandlerService);

  readonly petsHistory = input<AnimalHistory[] | null>(null);

  readonly errorMessage =
    input<string | null>(null);

  /*
   * Staff-only behaviors are disabled by default.
   * Shelter therefore retains plain animal names
   * and no impersonation controls.
   */
  readonly allowAnimalNavigation =
    input(false);

  readonly allowImpersonation =
    input(false);

  readonly isStaff = input(false);

  readonly petSurveySubmissions = input<SurveySubmission[] | null>(null);

  readonly viewAnimal =
    output<string>();

  readonly impersonate =
    output<string>();

  readonly petsFormatted =
    computed<AnimalHistoryDisplayItem[]>(
      () => {
        const pets = this.petsHistory();
        if (pets && pets.length > 0) {
          return this.formatPets(pets);
        } else {
          return [];
        }
      }
    );

  readonly surveySubmissionsFormatted = computed((): { petId: string, checkInsSummary: CheckInSummaryModel[] }[] => {
    const submissions = this.petSurveySubmissions();
    if (!submissions) {
      return [];
    }

    const groupedByPet = _.groupBy(submissions, s => s.petszelAnimalId.trim());

    return Object.keys(groupedByPet).map(key => {
      const surveys = groupedByPet[key];

      return {
        petId: key,
        checkInsSummary: surveys.map(s => ({
          surveyId: s.surveyId,
          surveyTitle: s.surveyTitle,
          statusIcon: generateCheckInStatusIcon(s.surveyTitle, s.partialSubmission, s.submissionData.surveyData)
        }))
      };
    });
  });

  getCheckInsByPet(petId: string): CheckInSummaryModel[] {
    return this.surveySubmissionsFormatted().find(s => s.petId === petId)?.checkInsSummary ?? [];
  }

  onViewAnimal(
    petszelAnimalId: string,
  ): void {
    const normalizedId =
      petszelAnimalId.trim();

    if (
      !this.allowAnimalNavigation() ||
      !normalizedId
    ) {
      return;
    }

    this.viewAnimal.emit(
      normalizedId,
    );
  }

  onImpersonate(
    petszelAnimalId: string,
  ): void {
    const normalizedId =
      petszelAnimalId.trim();

    if (
      !this.allowImpersonation() ||
      !normalizedId
    ) {
      return;
    }

    this.impersonate.emit(
      normalizedId,
    );
  }

  private formatPets(pets: AnimalHistory[]): AnimalHistoryDisplayItem[] {
    return pets.map((entry) => {
      const status =
        resolveAnimalEventStatus(
          entry.status,
        );

      return {
        id: entry.id.trim(),
        animalName:
          entry.animalName
            ?.trim() ||
          'Unnamed pet',

        animalDetails:
          this.formatAnimalDetails(
            entry,
          ),

        status:
        status.label,

        statusClass:
          this.getStatusClass(
            status.status,
          ),

        iconName:
          this.getAnimalIcon(
            entry.animalType,
          ),

        iconClass:
          this.getAnimalIconClass(
            entry.animalSex,
          ),

        canImpersonate: entry.canImpersonate,
        petszelAnimalId: entry.petszelAnimalId,
        shelterName: entry.shelterName,
        shelterId: entry.shelterId,
        eventsFormatted: entry.events.map(e => ({
          event: e.event,
          eventDate: e.eventDate ? this.datesHandlerService.formatUTCToCST(e.eventDate) : '',
          subEvent: e.subEvent,
          createdDate: e.createdDate ? this.datesHandlerService.formatUTCToCST(e.createdDate) : ''
        }))
      };
    });
  }

  private formatAnimalDetails(
    entry: AnimalHistory,
  ): string {
    const age =
      this.sharedPetUtilsService.calculateAge(entry.dateOfBirth);

    const breed =
      entry.animalBreed?.trim();

    return [age, breed]
      .filter(Boolean)
      .join(', ');
  }

  private getAnimalIcon(
    animalType: string,
  ): string {
    const normalizedType =
      animalType
        ?.trim()
        .toLowerCase();

    return normalizedType === 'cat'
      ? 'catAvatarIcon'
      : 'dogAvatarIcon';
  }

  private getAnimalIconClass(
    animalSex: string,
  ): string {
    return animalSex
      ?.trim()
      .toLowerCase() ===
      'female'
      ? 'survey-history__pet-icon--female'
      : 'survey-history__pet-icon--male';
  }

  private getStatusClass(
    status: AnimalEventStatus,
  ): string {
    return `survey-history__status--${status}`;
  }
}
